import { z } from "zod";
/**
 * Stable Transfer's legal states (`docs/products/stable.md` "Stable
 * Transfer": "Legal states: `PENDING -> ACCEPTED | DECLINED | CANCELLED |
 * EXPIRED`, then `ACCEPTED -> COMPLETED`"; `ARCHITECTURE.md` §13.6).
 *
 * In practice, `ACCEPTED` and `COMPLETED` are never observed as separate
 * durable states: acceptance re-checks capacity, creates the destination
 * Horse, freezes the origin Horse, and marks the transfer `COMPLETED` all
 * inside one transaction ("commits all authoritative state atomically" —
 * `ARCHITECTURE.md` §13.6 step 9). `ACCEPTED` stays a legal, testable state
 * in this module (and a real intermediate value the transition function
 * accepts) because the abstract state machine names it, but
 * `respondStableTransfer`'s acceptance path writes `COMPLETED` directly.
 */
export const STABLE_TRANSFER_STATUSES = [
    "PENDING",
    "ACCEPTED",
    "DECLINED",
    "CANCELLED",
    "EXPIRED",
    "COMPLETED",
];
export const StableTransferStatusSchema = z.enum(STABLE_TRANSFER_STATUSES);
const LEGAL_STABLE_TRANSFER_TRANSITIONS = {
    PENDING: ["ACCEPTED", "DECLINED", "CANCELLED", "EXPIRED"],
    ACCEPTED: ["COMPLETED"],
    DECLINED: [],
    CANCELLED: [],
    EXPIRED: [],
    COMPLETED: [],
};
export function isLegalStableTransferTransition(from, to) {
    return LEGAL_STABLE_TRANSFER_TRANSITIONS[from].includes(to);
}
/**
 * Whether a transfer request still occupies the "one active transfer per
 * Horse" slot (`docs/products/stable.md`: "the horse must be transferable
 * (not `DECEASED`, not already in an active transfer request)"). `ACCEPTED`
 * is included for completeness even though `respondStableTransfer` never
 * durably persists it standalone (see the module doc comment above).
 */
export function isStableTransferRequestActive(status) {
    return status === "PENDING" || status === "ACCEPTED";
}
/**
 * Computes the origin Horse's `record_inactive_until` grace-period deadline
 * (`docs/products/stable.md`: "a stored `inactive_until` grace-period
 * deadline (default 30 days, configurable)").
 */
export function computeTransferGraceDeadline(now, gracePeriodDays) {
    return new Date(now.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000);
}
